import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { FiMap, FiGrid, FiEye } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';
import JourneyMapCanvas from '../../components/canvas/JourneyMapCanvas';
import ExperienceMatrixCanvas from '../../components/canvas/ExperienceMatrixCanvas';

// ── Normalizers ───────────────────────────────────────────────────────────────
const normalizeJourneyPlacements = (rows) =>
  rows.map(p => ({
    id: p.id,
    x_position: p.x_position,
    y_position: p.y_position,
    label: p.metadata?.label || '',
    time_from_previous: p.metadata?.time_from_previous || null,
    order_index: p.metadata?.order_index ?? 0,
  })).sort((a, b) => a.order_index - b.order_index);

const normalizeMatrixPlacements = (rows, cards) =>
  rows.map(p => ({
    id: p.id,
    x_value: p.x_value,
    y_value: p.y_value,
    is_custom: p.is_custom || false,
    touchpoint_card_id: p.touchpoint_card_id || null,
    label: p.is_custom
      ? (p.custom_label || '')
      : (cards.find(c => c.id === p.touchpoint_card_id)?.content || ''),
  }));

// ── Session module helper ─────────────────────────────────────────────────────
// See ParticipantSession for rationale: never .maybeSingle() (throws on dup rows
// and cascades into more duplicates). Always pick earliest; re-select on race.
const getOrCreateSessionModule = async (sessionId, moduleId) => {
  const find = async () => {
    const { data } = await supabase
      .from(TABLES.SESSION_MODULES)
      .select('id')
      .eq('session_id', sessionId)
      .eq('module_id', moduleId)
      .order('started_at', { ascending: true })
      .limit(1);
    return data?.[0]?.id ?? null;
  };
  const existing = await find();
  if (existing) return existing;
  const { data: created, error } = await supabase
    .from(TABLES.SESSION_MODULES)
    .insert({ session_id: sessionId, module_id: moduleId, started_at: new Date().toISOString() })
    .select('id')
    .single();
  if (error) return await find();
  return created?.id ?? null;
};

const ActiveSession = () => {
  const { projectId, sessionId } = useParams();
  const navigate = useNavigate();

  const [modules, setModules] = useState([]);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [ending, setEnding] = useState(false);
  const [initDone, setInitDone] = useState(false);

  // Journey map state
  const [lanes, setLanes] = useState([]);
  const [journeyPlacements, setJourneyPlacements] = useState([]);

  // Experience matrix state
  const [cards, setCards] = useState([]);
  const [matrixPlacements, setMatrixPlacements] = useState([]);

  // Refs for stable polling closure
  const currentModuleIdRef = useRef(null);
  const currentModuleTypeRef = useRef(null);
  const sessionModuleIdRef = useRef(null);
  const lastUpdatedAtRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => { currentModuleIdRef.current = currentModuleId; }, [currentModuleId]);
  useEffect(() => { cardsRef.current = cards; }, [cards]);

  // ── Type-aware placement fetcher ──────────────────────────────────────────
  const fetchLivePlacements = useCallback(async (smId, moduleType) => {
    if (!smId) return;
    if (moduleType === 'journey_map') {
      const { data } = await supabase
        .from(TABLES.JOURNEY_MAP_PLACEMENTS)
        .select('*')
        .eq('session_module_id', smId);
      setJourneyPlacements(normalizeJourneyPlacements(data || []));
    } else if (moduleType === 'xy_plot') {
      const { data } = await supabase
        .from(TABLES.XY_PLOT_PLACEMENTS)
        .select('*')
        .eq('session_module_id', smId);
      setMatrixPlacements(normalizeMatrixPlacements(data || [], cardsRef.current));
    }
  }, []);

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await supabase.from(TABLES.SESSIONS).update({ status: 'active' }).eq('id', sessionId);

      const { data: mods } = await supabase
        .from(TABLES.MODULES).select('*').eq('project_id', projectId);
      const modList = mods || [];
      setModules(modList);

      const { data: state } = await supabase
        .from(TABLES.ACTIVE_SESSION_STATE)
        .select('*').eq('session_id', sessionId).maybeSingle();

      if (state?.current_module_id) {
        const mod = modList.find(m => m.id === state.current_module_id);
        if (mod) {
          setCurrentModuleId(mod.id);
          currentModuleIdRef.current = mod.id;
          currentModuleTypeRef.current = mod.type;
          setCurrentModule(mod);
          lastUpdatedAtRef.current = state.updated_at;

          if (mod.type === 'journey_map') {
            const { data: lanesData } = await supabase
              .from(TABLES.JOURNEY_MAP_LANES).select('*').eq('module_id', mod.id);
            setLanes(lanesData || []);
          } else if (mod.type === 'xy_plot') {
            const { data: cardData } = await supabase
              .from(TABLES.TOUCHPOINT_CARDS).select('*').eq('module_id', mod.id).order('order_index');
            setCards(cardData || []);
            cardsRef.current = cardData || [];
          }

          const smId = await getOrCreateSessionModule(sessionId, mod.id);
          sessionModuleIdRef.current = smId;
          await fetchLivePlacements(smId, mod.type);
        }
      }
      setInitDone(true);
    };
    init();
  }, [sessionId, projectId, fetchLivePlacements]);

  // ── Poll for participant changes ──────────────────────────────────────────
  useEffect(() => {
    if (!initDone) return;
    const poll = async () => {
      const { data, error } = await supabase
        .from(TABLES.ACTIVE_SESSION_STATE)
        .select('updated_at').eq('session_id', sessionId).maybeSingle();
      if (error || !data) return;

      const changed = data.updated_at !== lastUpdatedAtRef.current;
      if (changed && sessionModuleIdRef.current) {
        lastUpdatedAtRef.current = data.updated_at;
        fetchLivePlacements(sessionModuleIdRef.current, currentModuleTypeRef.current);
      }
    };
    const interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [initDone, sessionId, fetchLivePlacements]);

  // ── Select module ─────────────────────────────────────────────────────────
  const selectModule = async (mod) => {
    setCurrentModuleId(mod.id);
    currentModuleIdRef.current = mod.id;
    currentModuleTypeRef.current = mod.type;
    setCurrentModule(mod);
    setJourneyPlacements([]);
    setMatrixPlacements([]);

    await supabase.from(TABLES.ACTIVE_SESSION_STATE).upsert(
      { session_id: sessionId, current_module_id: mod.id, updated_at: new Date().toISOString() },
      { onConflict: 'session_id' }
    );

    if (mod.type === 'journey_map') {
      const { data: lanesData } = await supabase
        .from(TABLES.JOURNEY_MAP_LANES).select('*').eq('module_id', mod.id);
      setLanes(lanesData || []);
    } else if (mod.type === 'xy_plot') {
      const { data: cardData } = await supabase
        .from(TABLES.TOUCHPOINT_CARDS).select('*').eq('module_id', mod.id).order('order_index');
      setCards(cardData || []);
      cardsRef.current = cardData || [];
    }

    const smId = await getOrCreateSessionModule(sessionId, mod.id);
    sessionModuleIdRef.current = smId;
    await fetchLivePlacements(smId, mod.type);

    const { data: freshState } = await supabase
      .from(TABLES.ACTIVE_SESSION_STATE)
      .select('updated_at').eq('session_id', sessionId).maybeSingle();
    lastUpdatedAtRef.current = freshState?.updated_at ?? null;
  };

  // ── End session ───────────────────────────────────────────────────────────
  const endSession = async () => {
    setEnding(true);
    await supabase.from(TABLES.SESSIONS).update({ status: 'complete' }).eq('id', sessionId);
    navigate(`/projects/${projectId}/sessions`);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex">

      {/* Left: controls */}
      <div className="w-72 bg-white border-r border-gray-200 flex flex-col shadow-sm shrink-0">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Session Controls</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Select Module</p>
          <div className="space-y-2">
            {modules.length === 0
              ? <p className="text-sm text-gray-400 font-medium">No modules configured.</p>
              : modules.map(mod => {
                  const isActive = mod.id === currentModuleId;
                  return (
                    <button
                      key={mod.id}
                      onClick={() => selectModule(mod)}
                      className={`w-full text-left px-4 py-3 rounded-md border-2 flex items-center justify-between transition-all
                        ${isActive
                          ? 'border-rutabaga-pink bg-rutabaga-pink/5'
                          : 'border-gray-100 hover:border-rutabaga-pink/30 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon icon={mod.type === 'journey_map' ? FiMap : FiGrid}
                          className={isActive ? 'text-rutabaga-pink' : 'text-gray-300'} />
                        <div>
                          <p className={`font-bold text-sm ${isActive ? 'text-rutabaga-dark' : 'text-gray-500'}`}>
                            {mod.name}
                          </p>
                          <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest">
                            {mod.type === 'journey_map' ? 'Journey Map' : 'Touchpoint Mapping'}
                          </p>
                        </div>
                      </div>
                      {isActive && <span className="w-2 h-2 rounded-full bg-rutabaga-pink animate-pulse shrink-0" />}
                    </button>
                  );
                })}
          </div>
        </div>

        <div className="p-5 border-t border-gray-100">
          <button
            onClick={endSession}
            disabled={ending}
            className="w-full bg-red-50 text-red-600 font-black py-3 rounded-md hover:bg-red-100 transition-colors text-sm uppercase tracking-widest disabled:opacity-50"
          >
            {ending ? 'Ending…' : 'End Session'}
          </button>
        </div>
      </div>

      {/* Right: live view */}
      <div className="flex-1 bg-rutabaga-bg flex flex-col overflow-hidden">
        <div className="h-10 bg-white border-b border-gray-100 flex items-center px-5 gap-3 shrink-0">
          <SafeIcon icon={FiEye} className="text-rutabaga-pink text-sm" />
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Live Participant View</span>
          {currentModule && (
            <>
              <span className="text-gray-200">·</span>
              <span className="text-[10px] font-bold text-rutabaga-dark">{currentModule.name}</span>
            </>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Live</span>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {currentModule?.type === 'journey_map' && (
            <JourneyMapCanvas milestones={journeyPlacements} lanes={lanes} readonly />
          )}
          {currentModule?.type === 'xy_plot' && (
            <ExperienceMatrixCanvas
              cards={cards}
              placements={matrixPlacements}
              config={currentModule.config || {}}
              readonly
            />
          )}
          {!currentModule && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl mb-5">🎛️</div>
                <p className="font-black text-rutabaga-dark text-lg tracking-tight">Select a module to begin</p>
                <p className="text-gray-400 text-sm mt-2 font-medium max-w-xs mx-auto">
                  Choose a module from the left panel to push it to the participant&apos;s screen.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveSession;
